import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API_URL from "../../config/config";
import axios from "axios";
import {
  Box,
  Button,
  Heading,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  Notification,
  Layer,
} from "grommet";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import BreadcrumbHeader from "../../components/ManagerPortal/BreadcrumbHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { FormAdd, FormSubtract, Mail, UserAdd } from "grommet-icons";
import { SendInvite } from "../../components/ManagerPortal/SendInvite";

const EditCreatedAssessment = () => {
  const navigate = useNavigate();
  const { manager_id, assessment_id } = useParams();
  // const location = useLocation();

  // MAIN STATES
  // const assessment = location.state?.assessment || {};
  // const [assessment, setAssessment] = useState({});
  const [users, setUsers] = useState([]);
  const [assessmentDetails, setAssessmentDetails] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState(null);
  // const [dueDate, setDueDate] = useState(""); // Store assessment due date
  // const [dueTime, setDueTime] = useState(""); // Store assessment due time
  const [showConfirm, setShowConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Fetch assessment details and users on mount
  useEffect(() => {
    fetchAssessmentDetails();
    fetchUsersAndGroups();
  }, []);

  // (Optional) If you want to re-fetch when these setters change:
  useEffect(() => {
    fetchAssessmentDetails();
    fetchUsersAndGroups();
  }, [setSelectedUsers, setAssessmentDetails]);

  const fetchAssessmentDetails = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/manager/get_manager_assessments/${manager_id}`,
      );

      // Get the current date and time
      const now = new Date();

      // Filter assessments where due_by is in the future
      const validAssessments = response.data.assessments.filter(
        (assessment) => new Date(assessment.due_by) > now,
      );
      const singleAssessment = validAssessments.find(
        (assessment) => assessment.assessment_id === assessment_id,
      );
      console.log("Single assessment:", singleAssessment);
      // Store filtered response in local storage
      localStorage.setItem("assessments", JSON.stringify(singleAssessment));

      // Update state with filtered assessments
      setAssessmentDetails(singleAssessment);
      // setLoading(false);
    } catch (err) {
      console.error("Error fetching assessments data:", err);
      setError("Failed to load data");
      // setLoading(false);
    }
  };

  const fetchUsersAndGroups = async () => {
    try {
      const userResponse = await axios.get(
        `${API_URL}/manager/get_invited_users_status/${manager_id}`,
      );
      const usersSelectedForAssessment = await axios.get(
        `${API_URL}/manager/get_selected_users/${assessment_id}`,
      );
      const usersAlreadySelected =
        usersSelectedForAssessment.data.invites || [];
      const fetchedUsers = userResponse.data.invites || [];
      // Store selected user ids (matching the id from your API)
      const selectedUserIds = usersAlreadySelected.map((user) => user.user_id);
      setSelectedUsers(selectedUserIds);
      setUsers(fetchedUsers);
      setIsLoadingUsers(false);
    } catch (err) {
      console.error("Error fetching users and groups:", err);
      setError("Failed to fetch users and groups. Please try again.");
      setNotification({
        message: "Failed to fetch users and groups.",
        status: "critical",
      });
      setIsLoadingUsers(false);
    }
  };

  // Filter out users with declined invite (if invite_status === 2)
  const acceptedUsers = users.filter((user) => user.invite_status !== 2);

  const handleAddUser = async (userId) => {
    // Optimistically add the user locally
    setSelectedUsers((prevSelected) => [...prevSelected, userId]);
    try {
      await axios.post(`${API_URL}/manager/modify_assessment_details/`, {
        manager_id,
        assessment_id,
        selectedUsers: [userId],
      });

      setNotification({
        message: "User added successfully!",
        status: "normal",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error submitting selection:", error);
      setError("Failed to submit selection. Please try again.");
      setNotification({
        message: "Failed to submit selection. Please try again.",
        status: "critical",
      });
      window.location.reload();
    }
  };

  const handleRemoveUser = async (userId) => {
    setShowConfirm(false);
    try {
      await axios.delete(`${API_URL}/manager/modify_assessment_details/`, {
        data: {
          manager_id,
          assessment_id,
          selectedUsers: [userId],
        },
      });
      setUserToRemove(null);
      setSelectedUsers((prevSelected) =>
        prevSelected.filter((id) => id !== userId),
      );
      setNotification({
        message: "Assessment updated successfully!",
        status: "normal",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error submitting selection:", error);
      setError(error.response.data.error);
      setNotification({
        message: "Failed to submit selection. Please try again.",
        status: "critical",
      });
      window.location.reload();
    }
  };

  const initiateRemoveUser = (userId) => {
    setUserToRemove(userId);
    setShowConfirm(true);
  };

  const handleInviteAgain = (assessment_id, userId) => {
    try {
      axios.get(
        `${API_URL}/manager/send_assessment_reminder/${assessment_id}/${userId}`,
      );
      setNotification({
        message: "Reminder sent successfully!",
        status: "normal",
      });
      // Clear the notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      console.error("Error sending assessment reminder:", error);
      setNotification({
        message: "An error occurred while sending the assessment reminder.",
        status: "critical",
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    }
  };

  const handleSubmit = async () => {
    try {
      setNotification({
        message: "Assessment updated successfully!",
        status: "normal",
      });
      setTimeout(() => {
        navigate(`/${manager_id}/manager/assessments/`);
      }, 2000);
    } catch (error) {
      console.error("Error submitting selection:", error);
      setError("Failed to submit selection. Please try again.");
      setNotification({
        message: "Failed to submit selection. Please try again.",
        status: "critical",
      });
    }
  };

  // Determine whether to render the "Details" column (only if at least one user has assessmentInfo)
  const showDetailsColumn = assessmentDetails.users?.some(
    (user) => user.isComplete === 1 && user.assessmentInfo,
  );

  // Helper functions for the new columns:
  const getAssessmentAcceptedText = (isComplete) => {
    if (isComplete === 0) return "No";
    if (isComplete === 1) return "Yes";
    if (isComplete === -1) return "No";
    return "Yes";
  };

  const getAssessmentStatusText = (isComplete) => {
    if (isComplete === 0) return "Pending";
    if (isComplete === 1) return "Submitted";
    if (isComplete === -1) return "Declined";
    return "Pending";
  };

  // (Assuming you have a recommendation function; you can implement this as needed.)
  const handleAssessmentRecommendation = (assessmentId, userId) => {
    console.log("Handle recommendation for", assessmentId, userId);
    // Recommendation logic goes here
    navigate(`/manager/assessment-quiz-result/${userId}/${assessment_id}/`);
  };

  return (
    <Box fill="vertical" justify="between">
      <Box flex="grow" margin={{ bottom: "medium" }}>
        {/* Header Navigation */}
        <HeaderNavigation />
        <BreadcrumbHeader title1="Manage Assessments" title2="User Access" />

        {/* Main Body */}
        <Box flex="grow">
          {/* Error Message */}
          {error && (
            <Box background="status-critical" pad="small" round="small">
              <Text>{error}</Text>
            </Box>
          )}

          <Box
            direction="row"
            margin={{ top: "medium", horizontal: "medium" }}
            justify="between"
            align="center"
          >
            <Heading level="2" align="center">
              Assessment Name - {assessmentDetails.name}
            </Heading>
            <Box align="center">
              {/* <SendInvite assessment_id={assessment_id} /> */}
              <SendInvite
                manager_id={manager_id}
                isOpen={showInviteForm} // Control visibility
                users={users.length} 
                onClose={() => setShowInviteForm(false)} // Handle closing
                onUserAdded={() => {
                  setNotification({
                    message: "Redirecting to manage assessments",
                    status: "normal",
                  });
                  window.location.reload();
                }}
              />
            </Box>
          </Box>

          {/* Users Table */}
          <Box
            background="light-1"
            pad="medium"
            round="small"
            elevation="small"
            margin="medium"
          >
            <Heading level="2" margin="none">
              Team
            </Heading>
            <Box overflow="auto" background="light-1" round="small" pad="small">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell scope="col">
                      <Text weight="bold" size="medium">
                        User
                      </Text>
                    </TableCell>
                    <TableCell scope="col">
                      <Text weight="bold" size="medium">
                        Invite Status
                      </Text>
                    </TableCell>
                    <TableCell scope="col">
                      <Text weight="bold" size="medium">
                        Assessment Accepted
                      </Text>
                    </TableCell>
                    <TableCell scope="col">
                      <Text weight="bold" size="medium">
                        Assessment Status
                      </Text>
                    </TableCell>
                    {showDetailsColumn && (
                      <TableCell scope="col">
                        <Text weight="bold" size="medium">
                          Details
                        </Text>
                      </TableCell>
                    )}
                    <TableCell scope="col" align="center">
                      <Text weight="bold" size="medium">
                        Actions
                      </Text>
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingUsers ? (
                    <TableRow>
                      <TableCell colSpan={showDetailsColumn ? "6" : "5"}>
                        <Box
                          direction="row"
                          justify="center"
                          align="center"
                          pad="small"
                        >
                          <Spinner />
                          <Text margin={{ left: "small" }}>
                            Loading users...
                          </Text>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={showDetailsColumn ? "6" : "5"}
                        align="center"
                      >
                        No users available for this assessment.
                      </TableCell>
                    </TableRow>
                  ) : (
                    acceptedUsers.map((user) => {
                      // Look up this user’s assessment details (if they have been added)
                      const assessmentUser = assessmentDetails.users?.find(
                        (u) => u.user_id === user.user_id,
                      );
                      return (
                        <TableRow key={user.user_id}>
                          <TableCell>
                            {user.name}
                            <br />
                            {user.user_email}
                          </TableCell>
                          <TableCell>
                            {user.invite_status === 0
                              ? "Pending"
                              : user.invite_status === 1
                                ? "Accepted"
                                : user.invite_status === 2
                                  ? "Declined"
                                  : user.invite_status === 3
                                    ? "Active"
                                    : "Unknown"}
                          </TableCell>
                          <TableCell>
                            {assessmentUser
                              ? getAssessmentAcceptedText(
                                  assessmentUser.isComplete,
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {assessmentUser
                              ? getAssessmentStatusText(
                                  assessmentUser.isComplete,
                                )
                              : "N/A"}
                          </TableCell>
                          {showDetailsColumn && (
                            <TableCell>
                              {assessmentUser &&
                              assessmentUser.isComplete === 1 &&
                              assessmentUser.assessmentInfo ? (
                                <Box>
                                  <Text>
                                    {`Answered No: ${assessmentUser.assessmentInfo.correct_count}`}
                                  </Text>
                                  <Text>
                                    {`Answered Yes: ${assessmentUser.assessmentInfo.incorrect_count}`}
                                  </Text>
                                  <Text>
                                    {`End Time: ${assessmentUser.assessmentInfo.end_time}`}
                                  </Text>
                                  <Box
                                    align="start"
                                    margin={{ top: "small" }}
                                  ></Box>
                                </Box>
                              ) : (
                                <Text>N/A</Text>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {assessmentUser &&
                            assessmentUser.isComplete === 1 &&
                            assessmentUser.assessmentInfo ? (
                              <Button
                                label="Course recommendation"
                                onClick={() =>
                                  handleAssessmentRecommendation(
                                    assessmentDetails.assessment_id,
                                    user.user_id
                                  )
                                }
                                secondary
                                style={{ padding: "4px 12px" }}
                              />
                            ) : (
                              <Box direction="row" gap="medium" align="center" alignSelf="center">
                                {/* Add User Button */}
                                <Button
                                  // icon={<UserAdd />}
                                  icon={<FormAdd />}
                                  onClick={() => handleAddUser(user.user_id)}
                                  disabled={selectedUsers.includes(user.user_id)}
                                  title="Add user to assessment"
                                  color="status-ok"
                                  size="large"
                                />
                                {/* Remove User Button */}
                                <Button
                                  icon={<FormSubtract />}
                                  onClick={() => initiateRemoveUser(user.user_id)}
                                  disabled={!selectedUsers.includes(user.user_id)}
                                  title="Remove user from assessment"
                                  color="status-critical"
                                  size="large"
                                />
                                {/* Send Invite Button */}
                                <Button
                                  icon={<Mail />}
                                  // onClick={() => handleSendInvite(user.user_id)}
                                  onClick={() =>
                                      handleInviteAgain(
                                      assessmentDetails.assessment_id,
                                      user.user_id
                                      )
                                  }
                                  disabled={!selectedUsers.includes(user.user_id)}
                                  title="Send invite"
                                  size="large"
                                />
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Submit Button */}
          <Box
            direction="row"
            gap="medium"
            justify="center"
            margin={{ top: "medium" }}
          >
            <Button label="Finish editing" onClick={handleSubmit} primary />
          </Box>
        </Box>

        {/* Confirmation Dialog */}
        {showConfirm && (
          <Layer
            onEsc={() => setShowConfirm(false)}
            onClickOutside={() => setShowConfirm(false)}
            responsive
          >
            <Box pad="medium" gap="small" width="medium">
              <Heading level="3" margin="none">
                Confirm Removal
              </Heading>
              <Text>
                Are you sure you want to remove this user from the assessment?
              </Text>
              <Box direction="row" gap="medium" justify="end">
                <Button label="Cancel" onClick={() => setShowConfirm(false)} />
                <Button
                  label="Confirm"
                  onClick={() => handleRemoveUser(userToRemove)}
                  color="status-critical"
                />
              </Box>
            </Box>
          </Layer>
        )}
      </Box>
      <StickyFooter />
      {notification && (
        <Notification
          toast
          message={notification.message}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </Box>
  );
};

export default EditCreatedAssessment;
